import type { ComponentPropsWithRef } from "react";
import { forwardRef } from "react";
import { Img, Section } from "@react-email/components";
import { Link } from "@react-email/components";
import { omit } from "lodash";
import { t } from "@/i18n-js/instance";
import { useAttachmentMetadata } from "@/react/components/shared/BuilderUI/Media/AttachmentContext";
import { usePunditUserContext } from "@/react/contexts/punditUserContext";
import { getCommunityLogoUrlAndType } from "@/react/helpers/communityHelpers";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { parseSizing } from "@circle-react-shared/BuilderUI/utils/parseSizing";
import { useParseStyles } from "../../../Design/useParseStyles";
import { defaultStyles } from "./defaultStyles";
import logoPreviewImage from "./logo_preview.png";

interface LogoProps extends ComponentPropsWithRef<"table"> {
  alt?: string;
  "data-preview-mode": boolean;
  href?: string;
  image: {
    signed_id: string;
  };
  loading?: "lazy" | "eager";
  styles?: Record<string, StyleValue>;
}

export const Logo = forwardRef<HTMLTableElement, LogoProps>(
  (
    {
      image,
      styles = {},
      alt = "",
      href,
      loading = "lazy",
      "data-preview-mode": isPreviewMode,
      ...rest
    },
    ref,
  ) => {
    const { currentCommunity } = usePunditUserContext();
    const { url } = getCommunityLogoUrlAndType(currentCommunity, false);
    const attachmentMetadata = useAttachmentMetadata(image.signed_id);
    const { url: attachmentUrl } = attachmentMetadata ?? {};
    const imageUrl = attachmentUrl ?? url ?? logoPreviewImage;

    const altText =
      alt ||
      t("community_logo_alt", {
        community_name: currentCommunity?.name,
      });

    const inlineStyles = {
      ...useParseStyles({
        baseStyles: {
          width: "138px",
          height: "auto",
          display: "inline-block",
        },
        styles: omit(styles, ["sizing", "backgroundColor"]),
        defaultStyles,
      }),
      ...parseSizing({
        sizing: styles.sizing,
        defaultSizing: defaultStyles.sizing,
      }),
    };

    const sectionStyles = useParseStyles({
      baseStyles: {
        width: "100%",
        textAlign: "center" as const,
      },
      styles: {
        backgroundColor: styles.backgroundColor,
      },
      defaultStyles,
    });

    const LogoElement = (
      <Img
        src={imageUrl}
        alt={altText}
        style={inlineStyles}
        loading={loading}
      />
    );

    return (
      <Section ref={ref} style={sectionStyles} {...rest}>
        {href ? (
          <Link href={isPreviewMode ? undefined : href}>{LogoElement}</Link>
        ) : (
          LogoElement
        )}
      </Section>
    );
  },
);

Logo.displayName = "Logo";
